import { useEffect, useState } from 'react'
import type { AuthUser } from 'grabbitbot-legacy-ui'
import { paymentClient, portalClient } from '../api'
import { CONNECTIONS_CHANGED_EVENT } from '../api/connectionEvents'
import type { DefaultConfig } from '../api/types'
import { isCanceledButActive } from '../pages/payment/planStatus'

/** Legacy `V3ExchangeService.exchanges` label map
 * (`v3-exchange.service.ts:24-27`) — cosmetic exchange-id -> display-name
 * lookup, ported verbatim. */
const EXCHANGE_LABELS: Record<string, string> = {
  binance: 'Binance',
  wnfexchange: 'WnF Exchange',
}

export interface ExchangeOption {
  exchangeId: string
  label: string
}

function labelFor(item: DefaultConfig): string {
  const base = (item.exchange && EXCHANGE_LABELS[item.exchange]) || item.exchange || item.exchangeId
  const suffix = item.exchangeSubId ? ` (${item.name ?? item.exchangeSubId})` : ''
  return `${base}${suffix}`
}

/** Legacy `V3HeaderComponent.isFreeUser` (`v3-header.component.ts:47-50`) —
 * internal WebbAndFlow accounts never see the payment-required banner. */
function isFreeUser(user: AuthUser): boolean {
  return user.username.includes('@webbandflow.co.uk')
}

/**
 * Header data that used to live in `V3ExchangeService` + `V3HeaderComponent`
 * (`refreshData()`): the exchange picker's option list + selection, and
 * whether the payment-required banner should show (and in which variant).
 * Ported behaviour, not byte-for-byte code — see each field's source
 * comment. Fails safe on any read error: an unreachable exchange-picker or
 * payment-probe endpoint degrades to "no exchanges configured yet" /
 * "no banner" rather than breaking the whole header (round 1 has no backend
 * yet for the v3 config-default route in every environment — see README
 * "Known gaps"; a broken header would block every other page).
 */
export function usePortalHeader(user: AuthUser | null, exchangeIdParam: string | undefined, tradingTypeParam: string | undefined) {
  const tradingType = tradingTypeParam || 'spot'
  const [exchanges, setExchanges] = useState<ExchangeOption[] | null>(null)
  const [selectedExchangeId, setSelectedExchangeId] = useState('')
  const [paymentRequired, setPaymentRequired] = useState(false)
  const [canceledUntil, setCanceledUntil] = useState<string | null>(null)
  // Bumped when a start / delete changed the connections (the picker keeps
  // its own list; without this it showed the old entries until a reload).
  const [connectionsVersion, setConnectionsVersion] = useState(0)

  useEffect(() => {
    const bump = () => setConnectionsVersion((n) => n + 1)
    window.addEventListener(CONNECTIONS_CHANGED_EVENT, bump)
    return () => window.removeEventListener(CONNECTIONS_CHANGED_EVENT, bump)
  }, [])

  useEffect(() => {
    let cancelled = false

    portalClient
      .searchDefaultConfigs(tradingType)
      .then((result) => {
        if (cancelled) return
        const options = result.map((item) => ({ exchangeId: item.exchangeId, label: labelFor(item) }))
        setExchanges(options)

        // Ported selection algorithm (`V3ExchangeService.loadExchanges`):
        // URL param wins if it names a configured exchange; otherwise fall
        // back to the last exchange remembered in localStorage; otherwise
        // the first configured exchange.
        let chosen = exchangeIdParam && options.some((o) => o.exchangeId === exchangeIdParam) ? exchangeIdParam : ''
        if (!chosen) {
          const last = localStorage.getItem('lastExchangeId') || ''
          if (last && options.some((o) => o.exchangeId === last)) chosen = last
        }
        if (!chosen && options.length > 0) chosen = options[0]?.exchangeId ?? ''
        setSelectedExchangeId(chosen)
        if (chosen) {
          localStorage.setItem('lastExchangeId', chosen)
          localStorage.setItem('lastTradingType', tradingType)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExchanges([])
          setSelectedExchangeId('')
        }
      })

    return () => {
      cancelled = true
    }
  }, [exchangeIdParam, tradingType, connectionsVersion])

  useEffect(() => {
    if (!user) {
      setPaymentRequired(false)
      setCanceledUntil(null)
      return
    }
    let cancelled = false

    // Ported `V3HeaderComponent.refreshData()`, now FeeSubscription-only (S6,
    // plan 2026-10-17): `paymentRequired` for non-free users is decided from
    // whether any current-stack plan has an active subscription
    // (`subscription.status === 1`, or a canceled one -- status 6 -- whose
    // paid period still runs; `subscription` may be `null`). The old local
    // `/payment/old/subscription/*` probe is gone. A failed plans read or a
    // non-list answer never shows the banner (fail open -- an outage must not
    // nag every signed-in user).
    setPaymentRequired(false)
    setCanceledUntil(null)
    if (isFreeUser(user)) return
    paymentClient
      .listPlans()
      .then((plans) => {
        if (cancelled || !Array.isArray(plans)) return
        setPaymentRequired(!plans.some((plan) => plan.subscription?.status === 1 || isCanceledButActive(plan.subscription)))
        // No running subscription, only canceled-but-paid ones: say until when.
        const paidUntil = plans
          .filter((plan) => isCanceledButActive(plan.subscription))
          .map((plan) => plan.subscription?.expiresAt ?? '')
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
        setCanceledUntil(plans.some((plan) => plan.subscription?.status === 1) ? null : (paidUntil ?? null))
      })
      .catch(() => {
        if (!cancelled) setPaymentRequired(false)
      })

    return () => {
      cancelled = true
    }
  }, [user])

  return { tradingType, exchanges, selectedExchangeId, paymentRequired, canceledUntil }
}
